import { Controller } from "@hotwired/stimulus"
import { createUserChannel } from "../../../../../app/javascript/channels/user_channel"

export default class extends Controller {
  static targets = ["channels", "chat", "messages", "newChannel", "participants", "entries", "search", "unreadFilter", "more", "error", "older", "newMessages"]
  static values = {
    channelId: Number, currentUserId: Number, organizationId: Number, channelUrl: String, inboxUrl: String, unreadCounts: Object,
    // Turbo preserves the panel's DOM across visits, including these inbox settings.
    section: { type: String, default: "conversations" },
    page: { type: Number, default: 1 },
    unreadOnly: { type: Boolean, default: false }
  }

  connect() {
    this.active = true
    this.isInChat = this.chatTarget.classList.contains("active")
    this.messageRequests = new Map()
    this.lastRead = new Map()
    this.events = new AbortController()
    const options = { signal: this.events.signal }
    this.element.addEventListener("shown.bs.drawer", () => {
      this.refreshInbox()
      this.markVisibleRead()
    }, options)
    document.addEventListener("visibilitychange", () => { if (!document.hidden) { this.refreshInbox(); this.markVisibleRead() } }, options)
    document.addEventListener("turbo:load", () => this.updateGlobalUnreadBadge(), options)
    document.addEventListener("notifications:sync", event => {
      this.unreadCountsValue = event.detail
      this.updateGlobalUnreadBadge()
      this.scheduleRefresh()
    }, options)
    this.element.addEventListener("communication:sent", event => {
      const { channelId, message } = event.detail
      if (this.isInChat && this.channelIdValue === channelId) {
        this.upsertMessage(message.html, true)
        this.scrollToBottom()
      }
      this.scheduleRefresh()
    }, options)
    this.subscription = createUserChannel(this.currentUserIdValue, data => this.received(data), () => {
      if (!this.active) return
      this.scheduleRefresh()
      this.refreshMountedMessages()
    })
    this.updateGlobalUnreadBadge()
  }

  disconnect() {
    this.active = false
    this.events.abort()
    this.subscription?.unsubscribe()
    this.inboxRequest?.abort()
    this.openRequest?.abort()
    clearTimeout(this.refreshTimer)
    clearTimeout(this.searchTimer)
  }

  async request(url, options = {}) {
    const response = await fetch(url, {
      ...options,
      headers: { Accept: "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "", ...options.headers }
    })
    if (!response.ok || response.redirected) throw new Error("Unable to load messages. Please try again.")
    return response
  }

  received(data) {
    if (data.event === "message.created" || data.event === "message.updated") {
      this.scheduleRefresh()
      if (this.isInChat && this.channelIdValue === Number(data.channel_id)) {
        this.fetchMessage(data.message.id, data.event === "message.created")
      }
    } else if (data.event === "channel.updated" || data.event === "channel.read") {
      this.scheduleRefresh()
    } else if (data.event === "messages.synced") {
      data.messages.forEach(entry => {
        if (this.isInChat && this.channelIdValue === Number(entry.channel_id)) this.fetchMessage(entry.message.id, true)
      })
    }
  }

  scheduleRefresh() {
    clearTimeout(this.refreshTimer)
    this.refreshTimer = setTimeout(() => { if (this.active) this.refreshInbox() }, 150)
  }

  async refreshInbox() {
    // A filter or pagination action already uses the latest search text. Don't
    // let an older debounce reset the loaded pages after that request finishes.
    clearTimeout(this.searchTimer)
    this.inboxRequest?.abort()
    const request = new AbortController()
    this.inboxRequest = request
    this.entriesTarget.setAttribute("aria-busy", "true")
    try {
      const pages = await Promise.all(Array.from({ length: this.pageValue }, async (_, index) => {
        const url = new URL(this.inboxUrlValue, window.location.origin)
        url.search = new URLSearchParams({ section: this.sectionValue, q: this.searchTarget.value, unread: this.unreadOnlyValue ? "1" : "0", page: index + 1 })
        return (await this.request(url, { signal: request.signal })).json()
      }))
      if (!this.active || request.signal.aborted) return
      const last = pages[pages.length - 1]
      this.entriesTarget.innerHTML = pages.map(page => page.html).join("")
      this.moreTarget.classList.toggle("d-none", !last.has_more)
      this.moreTarget.disabled = false
      this.unreadCountsValue = last.unread_counts
      Object.entries(last.counts).forEach(([section, count]) => {
        const badge = this.element.querySelector(`[data-section-count="${section}"]`)
        badge.textContent = count
        badge.classList.toggle("d-none", count === 0)
      })
      this.updateGlobalUnreadBadge()
      this.clearError()
    } catch (error) {
      if (error.name !== "AbortError") this.showError(error.message)
    } finally {
      if (this.inboxRequest === request) {
        this.entriesTarget.setAttribute("aria-busy", "false")
        this.moreTarget.disabled = false
      }
    }
  }

  changeSection(event) {
    this.sectionValue = event.currentTarget.dataset.section
    this.element.querySelectorAll("[data-section]").forEach(button => button.setAttribute("aria-pressed", String(button.dataset.section === this.sectionValue)))
    this.pageValue = 1
    this.refreshInbox()
  }

  searchInbox() {
    clearTimeout(this.searchTimer)
    this.searchTimer = setTimeout(() => { this.pageValue = 1; this.refreshInbox() }, 200)
  }

  toggleUnread() {
    this.unreadOnlyValue = !this.unreadOnlyValue
    this.unreadFilterTarget.setAttribute("aria-pressed", String(this.unreadOnlyValue))
    this.unreadFilterTarget.classList.toggle("active", this.unreadOnlyValue)
    this.pageValue = 1
    this.refreshInbox()
  }

  loadMore() {
    this.moreTarget.disabled = true
    this.pageValue += 1
    this.refreshInbox()
  }

  async markInboxRead(event) {
    const button = event.currentTarget
    button.disabled = true
    try {
      await this.request(`${this.inboxUrlValue}/mark_all_read`, { method: "POST", body: new URLSearchParams({ section: this.sectionValue }) })
      await this.refreshInbox()
    } catch (_error) {
      this.showError("Could not mark messages as read. Please try again.")
    } finally {
      button.disabled = false
    }
  }

  openChannel(event) {
    this.openChannelById(event.currentTarget.dataset.channelId, event.currentTarget.dataset.messageId)
  }

  async openChannelById(channelId, messageId = null) {
    this.openRequest?.abort()
    const request = new AbortController()
    this.openRequest = request
    const url = new URL(this.channelUrl(channelId), window.location.origin)
    if (messageId) url.searchParams.set("message_id", messageId)
    this.element.setAttribute("aria-busy", "true")
    try {
      const response = await this.request(url, { signal: request.signal, headers: { Accept: "text/html" } })
      const html = await response.text()
      if (!this.active || request.signal.aborted) return
      this.chatTarget.innerHTML = html
      this.channelIdValue = Number(channelId)
      this.setPanel("chat")
      this.regroupDays()
      this.scrollToBottom()
      this.clearError()
      this.markVisibleRead()
    } catch (error) {
      if (error.name !== "AbortError") this.showError(error.message)
    } finally {
      if (this.openRequest === request) this.element.removeAttribute("aria-busy")
    }
  }

  async fetchMessage(messageId, created = false) {
    const channelId = this.channelIdValue
    const key = `${channelId}:${messageId}`
    // A delivery update can arrive while the creation fetch is still in flight.
    if (this.messageRequests.has(key)) {
      this.messageRequests.set(key, true)
      return
    }
    this.messageRequests.set(key, false)
    try {
      const response = await this.request(`${this.channelUrl(channelId)}/${messageId}`)
      const message = await response.json()
      if (!this.active || !this.isInChat || this.channelIdValue !== channelId || !this.hasMessagesTarget) return
      const existing = this.messagesTarget.querySelector(`[data-message-id="${messageId}"]`)
      const first = this.messagesTarget.querySelector("[data-message-id]")
      if (!existing && first && Number(messageId) < Number(first.dataset.messageId)) return
      if (existing || this.messagesTarget.dataset.hasNewer !== "true") {
        this.upsertMessage(message.html)
      } else if (created) {
        this.newMessagesTarget.classList.remove("d-none")
      }
      this.markVisibleRead()
    } catch (_error) {
      if (this.active) this.showError("A message could not be refreshed. Reopen the conversation to retry.")
    } finally {
      const again = this.messageRequests.get(key)
      this.messageRequests.delete(key)
      if (again && this.active && this.channelIdValue === channelId) this.fetchMessage(messageId)
    }
  }

  refreshMountedMessages() {
    if (!this.isInChat || !this.hasMessagesTarget) return
    // Refresh only queued messages; notification replay supplies missed new messages.
    this.messagesTarget.querySelectorAll('[data-delivery-status="queued"], [data-external="true"][data-delivery-status="sent"]').forEach(message => this.fetchMessage(message.dataset.messageId))
  }

  upsertMessage(html, forceScroll = false) {
    if (!this.hasMessagesTarget) return
    const stickToBottom = forceScroll || (this.isViewing() && this.nearBottom())
    const template = document.createElement("template")
    template.innerHTML = html.trim()
    const message = template.content.querySelector("[data-message-id]")
    if (!message) return
    const id = Number(message.dataset.messageId)
    const list = this.messagesTarget.querySelector(".message-list")
    const existing = list.querySelector(`[data-message-id="${id}"]`)
    if (existing) existing.replaceWith(message)
    else {
      const next = Array.from(list.querySelectorAll("[data-message-id]")).find(node => Number(node.dataset.messageId) > id)
      list.insertBefore(message, next || null)
      this.messagesTarget.querySelector(".messages-empty")?.remove()
    }
    this.regroupDays()
    if (stickToBottom) this.scrollToBottom()
    else if (!existing) this.newMessagesTarget.classList.remove("d-none")
  }

  regroupDays() {
    const list = this.messagesTarget.querySelector(".message-list")
    list.querySelectorAll(".message-day").forEach(node => node.remove())
    let previousDay = null
    list.querySelectorAll("[data-message-id]").forEach(message => {
      if (message.dataset.day !== previousDay) {
        const divider = document.createElement("div")
        divider.className = "message-day"
        divider.textContent = message.dataset.dayLabel
        message.before(divider)
        previousDay = message.dataset.day
      }
    })
  }

  async loadOlder(event) {
    const button = event.currentTarget
    const channelId = this.channelIdValue
    const container = this.messagesTarget
    const first = this.messagesTarget.querySelector("[data-message-id]")
    if (!first || button.disabled) return
    button.disabled = true
    const height = this.messagesTarget.scrollHeight
    const top = this.messagesTarget.scrollTop
    try {
      const response = await this.request(`${this.channelUrl(channelId)}?before_id=${first.dataset.messageId}`)
      const page = await response.json()
      if (!this.active || this.channelIdValue !== channelId || !this.isInChat || this.messagesTarget !== container) return
      this.messagesTarget.querySelector(".message-list").insertAdjacentHTML("afterbegin", page.html)
      this.regroupDays()
      button.classList.toggle("d-none", !page.has_older)
      this.messagesTarget.scrollTop = top + this.messagesTarget.scrollHeight - height
    } catch (_error) {
      this.showError("Earlier messages could not be loaded. Please try again.")
    } finally {
      button.disabled = false
    }
  }

  jumpToLatest() {
    if (this.messagesTarget.dataset.hasNewer === "true") this.openChannelById(this.channelIdValue)
    else { this.scrollToBottom(); this.markVisibleRead() }
  }

  onScroll() {
    if (this.nearBottom()) {
      if (this.messagesTarget.dataset.hasNewer !== "true") this.newMessagesTarget.classList.add("d-none")
      this.markVisibleRead()
    }
  }

  async markVisibleRead() {
    if (!this.isViewing() || !this.nearBottom()) return
    const channelId = this.channelIdValue
    const last = this.messagesTarget.querySelector("[data-message-id]:last-child")
    const throughId = Number(last?.dataset.messageId)
    if (!throughId || this.lastRead.get(channelId) === throughId) return
    this.lastRead.set(channelId, throughId)
    try {
      await this.request(`${this.channelUrl(channelId)}/mark_all_read`, { method: "POST", body: new URLSearchParams({ through_id: throughId }) })
      this.scheduleRefresh()
    } catch (_error) {
      this.lastRead.delete(channelId)
      this.showError("Read status could not be saved. Reopen the conversation to retry.")
    }
  }

  isViewing() {
    return this.isInChat && this.hasMessagesTarget && this.element.open && !document.hidden
  }

  nearBottom() {
    return this.hasMessagesTarget && this.messagesTarget.scrollHeight - this.messagesTarget.scrollTop - this.messagesTarget.clientHeight < 60
  }

  scrollToBottom() {
    if (!this.hasMessagesTarget) return
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
    if (this.messagesTarget.dataset.hasNewer !== "true") this.newMessagesTarget.classList.add("d-none")
  }

  async newChannel(event) {
    event.preventDefault()
    try {
      const response = await this.request(event.currentTarget.href, { headers: { Accept: "text/html" } })
      const html = await response.text()
      if (!this.active) return
      this.newChannelTarget.innerHTML = html
      this.setPanel("newChannel")
      // The picker is still sliding in; ordinary focus scrolls its clipped parent sideways.
      this.newChannelTarget.querySelector("input")?.focus({ preventScroll: true })
    } catch (error) { this.showError(error.message) }
  }

  searchParticipants(event) {
    const term = event.target.value.toLowerCase()
    this.participantsTarget.querySelectorAll(".participant").forEach(participant => participant.classList.toggle("d-none", !participant.textContent.toLowerCase().includes(term)))
  }

  async createNewChannel(event) {
    event.preventDefault()
    const link = event.currentTarget
    if (this.creating) return
    this.creating = true
    try {
      const data = await (await this.request(link.href, { method: "POST" })).json()
      await this.openChannelById(data.id)
      this.scheduleRefresh()
    } catch (error) { this.showError(error.message) }
    finally { this.creating = false }
  }

  newChannelToChannels() { this.backToChannels() }

  backToChannels() {
    this.openRequest?.abort()
    this.setPanel("channels")
    this.refreshInbox()
  }

  setPanel(panel) {
    this.isInChat = panel === "chat"
    this.channelsTarget.classList.toggle("slide-left", panel !== "channels")
    this.channelsTarget.inert = panel !== "channels"
    this.chatTarget.classList.toggle("active", panel === "chat")
    this.chatTarget.inert = panel !== "chat"
    this.newChannelTarget.classList.toggle("active", panel === "newChannel")
    this.newChannelTarget.inert = panel !== "newChannel"
    const viewport = this.element.querySelector(".drawer-body")
    viewport.scrollLeft = 0
    viewport.scrollTop = 0
  }

  channelUrl(channelId) { return this.channelUrlValue.replace(/CHANNEL_ID/g, channelId) }

  updateGlobalUnreadBadge() {
    const badge = document.getElementById("global_message_count")
    if (!badge) return
    const count = Object.values(this.unreadCountsValue).reduce((sum, value) => sum + Number(value), 0)
    badge.textContent = count
    badge.classList.toggle("d-none", count === 0)
    badge.previousElementSibling?.classList.toggle("fa-beat", count > 0)
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("d-none")
  }

  clearError() { this.errorTarget.classList.add("d-none") }
}
